import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import AdminLayout from "../../layout/AdminLayout";
import PageHeader from "../../common/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import * as model from "../../../models/ClientsModel";
import { MyReactSelectMultiple } from "../../inputs/inputs";
import { apiError } from "../../../helpers/ErrorHelper";

const module = {
  title: "Merge Clients",
  singularTitle: "Client",
  key: "clients",
  route: "clients",
  primaryCol: "_client_id",
};

const impactLabels = {
  client_branches: "Billing branches to move",
  invoices: "Invoices (client remapped, branch unchanged)",
  requirements: "Requirements",
  client_contacts: "Contacts / POCs",
  interviews: "Interviews",
  meetings: "Meetings",
  onboards: "Onboards",
  candidate_requirement_mapping: "Candidate mappings",
  candidates: "Candidates",
  user_clients: "User access rows",
};

export default function ClientMerge() {
  const { state } = useAuth();
  const user_data = state.user_data;
  const navigate = useNavigate();

  const isAdmin = user_data?.urole_type === "admin";

  const [clientOptions, setClientOptions] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [fields, setFields] = useState({ selected_clients: [] });
  const [survivorId, setSurvivorId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [branchRenames, setBranchRenames] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Only administrators can merge clients.");
      navigate("/clients");
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    const loadClients = async () => {
      setLoadingClients(true);
      try {
        const response = await model.getData({
          user_token: user_data.user_token,
          page: 1,
          limit: 500,
          isTrash: 0,
        });
        const rows = response?.clients || [];
        setClientOptions(
          rows.map((row) => ({
            value: String(row._client_id),
            label: `#${row._client_id} — ${row.client_name}${row.client_gstin ? ` (${row.client_gstin})` : ""}`,
            data: row,
          }))
        );
      } catch (error) {
        apiError(error);
      }
      setLoadingClients(false);
    };

    if (user_data?.user_token) {
      loadClients();
    }
  }, [user_data?.user_token]);

  const selectedClients = fields.selected_clients || [];

  const selectedIds = useMemo(
    () => selectedClients.map((item) => Number(item.value)).filter((id) => id > 0),
    [selectedClients]
  );

  const duplicateIds = useMemo(
    () => selectedIds.filter((id) => String(id) !== String(survivorId)),
    [selectedIds, survivorId]
  );

  useEffect(() => {
    if (selectedIds.length < 2) {
      setSurvivorId("");
      setPreview(null);
      return;
    }
    if (!survivorId || !selectedIds.includes(Number(survivorId))) {
      setSurvivorId(String(selectedIds[0]));
    }
    setPreview(null);
  }, [selectedIds, survivorId]);

  const handlePreview = async () => {
    if (selectedIds.length < 2) {
      toast.warning("Select at least two clients to merge.");
      return;
    }
    if (!survivorId) {
      toast.warning("Choose the primary client to keep.");
      return;
    }
    if (duplicateIds.length < 1) {
      toast.warning("Choose at least one other client besides the primary.");
      return;
    }

    setPreviewLoading(true);
    setPreview(null);
    try {
      const response = await model.mergePreview(user_data.user_token, {
        survivor_client_id: Number(survivorId),
        duplicate_client_ids: duplicateIds,
      });
      if (!response.status) {
        toast.error(response.message || "Could not load merge preview.");
        return;
      }
      const data = response.data?.merge_preview;
      setPreview(data);
      const renames = {};
      (data?.branches || []).forEach((branch) => {
        renames[branch._client_branch_id] = branch.client_branch_name || "";
      });
      setBranchRenames(renames);
    } catch (error) {
      apiError(error);
      if (error.response?.data?.invalid_token == 1) {
        navigate("/re-login");
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleMerge = async () => {
    setMergeLoading(true);
    try {
      const payload = {
        survivor_client_id: Number(survivorId),
        duplicate_client_ids: duplicateIds,
        branch_renames: branchRenames,
      };
      const response = await model.mergeClients(user_data.user_token, payload);
      if (!response.status) {
        toast.error(response.message || "Merge failed.");
        return;
      }
      toast.success(response.message || "Clients merged successfully.");
      localStorage.removeItem(module.key);
      navigate("/clients");
    } catch (error) {
      apiError(error);
      if (error.response?.data?.invalid_token == 1) {
        navigate("/re-login");
      }
    } finally {
      setMergeLoading(false);
      setShowConfirm(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>{module.title} - Digital CMS</title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid">
          <PageHeader module={module} />

          <div className="row">
            <div className="col-xl-12">
              <div className="card custom-card">
                <div className="card-header">
                  <div className="card-title">Merge duplicate clients</div>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-4">
                    Combine duplicate client records into one primary client. Billing branches move to the
                    primary client; invoices keep their original branch for PDF history. Requirements,
                    interviews, meetings, and contacts are remapped to the primary client.
                  </p>

                  <div className="row g-4">
                    <div className="col-lg-6">
                      <MyReactSelectMultiple
                        column="selected_clients"
                        label="Clients to combine"
                        options={clientOptions}
                        fields={fields}
                        setFields={setFields}
                        required={true}
                        disabled={loadingClients}
                        firstOption="Search and select clients (minimum 2)"
                      />
                      {loadingClients && (
                        <small className="text-muted">Loading client list…</small>
                      )}
                    </div>

                    <div className="col-lg-6">
                      <label className="form-label">
                        Primary client to keep <span className="text-danger">*</span>
                      </label>
                      {selectedIds.length < 2 ? (
                        <p className="text-muted mb-0">Select at least two clients first.</p>
                      ) : (
                        <div className="list-group">
                          {selectedClients.map((item) => (
                            <label
                              key={item.value}
                              className="list-group-item list-group-item-action d-flex align-items-center gap-2"
                            >
                              <input
                                type="radio"
                                name="survivor_client"
                                className="form-check-input m-0"
                                checked={String(survivorId) === String(item.value)}
                                onChange={() => {
                                  setSurvivorId(String(item.value));
                                  setPreview(null);
                                }}
                              />
                              <span>{item.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mt-4">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={previewLoading || selectedIds.length < 2}
                      onClick={handlePreview}
                    >
                      {previewLoading ? "Loading preview…" : "Preview merge"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => navigate("/clients")}
                    >
                      Cancel
                    </button>
                  </div>

                  {preview && (
                    <div className="mt-4 border rounded p-3">
                      <h6 className="mb-3">Merge preview</h6>

                      {(preview.warnings || []).length > 0 && (
                        <div className="alert alert-warning">
                          <ul className="mb-0 ps-3">
                            {preview.warnings.map((warning, index) => (
                              <li key={index}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="table-responsive mb-4">
                        <table className="table table-sm table-bordered">
                          <thead>
                            <tr>
                              <th>Record type</th>
                              <th className="text-end">Rows affected</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(preview.impact || {}).map(([key, count]) => (
                              <tr key={key}>
                                <td>{impactLabels[key] || key}</td>
                                <td className="text-end">{count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {(preview.poc_collisions || []).length > 0 && (
                        <>
                          <h6 className="mb-2">POC / contact collisions</h6>
                          <div className="table-responsive mb-4">
                            <table className="table table-sm table-bordered">
                              <thead>
                                <tr>
                                  <th>Contact</th>
                                  <th>Email / Mobile</th>
                                  <th>From client</th>
                                </tr>
                              </thead>
                              <tbody>
                                {preview.poc_collisions.map((row) => (
                                  <tr key={row.duplicate_contact_id}>
                                    <td>{row.contact_name}</td>
                                    <td>
                                      {[row.contact_email, row.contact_mobile]
                                        .filter(Boolean)
                                        .join(" / ")}
                                    </td>
                                    <td>#{row.from_client_id}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}

                      <h6 className="mb-2">Billing branches after merge</h6>
                      <p className="text-muted small">
                        You can rename branches before merging. Invoice PDFs still use the branch they were
                        issued under.
                      </p>
                      <div className="table-responsive mb-3">
                        <table className="table table-sm table-bordered">
                          <thead>
                            <tr>
                              <th>Branch ID</th>
                              <th>From client</th>
                              <th>Name (editable)</th>
                              <th>GSTIN</th>
                              <th>Default</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(preview.branches || []).map((branch) => (
                              <tr key={branch._client_branch_id}>
                                <td>{branch._client_branch_id}</td>
                                <td>#{branch.client_branch_client_id}</td>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={branchRenames[branch._client_branch_id] ?? ""}
                                    onChange={(e) =>
                                      setBranchRenames((prev) => ({
                                        ...prev,
                                        [branch._client_branch_id]: e.target.value,
                                      }))
                                    }
                                  />
                                </td>
                                <td>{branch.client_branch_gstin || "—"}</td>
                                <td>{branch.client_branch_is_default == 1 ? "Yes" : "No"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={mergeLoading}
                        onClick={() => setShowConfirm(true)}
                      >
                        Merge clients
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showConfirm} onHide={() => !mergeLoading && setShowConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm merge</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Merge {duplicateIds.length} client(s) into client #{survivorId}? Duplicate client rows will
            be archived. This cannot be undone easily.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setShowConfirm(false)}
            disabled={mergeLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleMerge}
            disabled={mergeLoading}
          >
            {mergeLoading ? (
              <span>
                <span className="spinner-border spinner-border-sm align-middle"></span> Merging…
              </span>
            ) : (
              "Yes, merge"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </AdminLayout>
  );
}
